import * as React from 'react';

import { HonoraryNote, HonoraryNoteLine } from '../../../../store/details/honorary-note/types';
import { addHonoraryNoteLine, saveHonoraryNote, updateHonoraryReference, deleteHonoraryNoteLine, updateHonoraryStructuredMessage } from '../../../../store/details/honorary-note/actions';

import { BelfiusCurrencyInput } from '../../../custom/belfius-currency-input';
import BelfiusCurrencyText from '../../../custom/belfius-currency-text';
import { BelfiusDecimalInput } from '../../../custom/belfius-decimal-input';
import { ConnectedReduxProps, ApplicationState } from '../../../../store';
import DatePicker from 'react-date-picker';
import { Dispatch } from 'redux';
import { FormControl, SelectChangeEvent } from '@mui/material';
import { Ii18n } from '../../../../store/utils/contracts/i18n.interface';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { connect } from 'react-redux';
import { getI18n } from '../../../../store/utils/i18n/strings.manager';
import { getTypeCodesForLanguage, getTypeCodeTranslation } from '../../../../store/utils/typecodes/typecode-translations';
import styles from './honorary-note-detail.module.css';
import * as honoraryNoteLogic from '../../../../business/logic/honorary-note-logic';
import { TypeCodeTranslation } from '../../../../business/models/shared.model';
import { BusinessValidation, BusinessValidationType } from '../../../../business/models/policy-business.model';
import FormattedInputStructuredMessage from '../../../custom/FormattedInputStructuredMessage';

interface PropsFromState {
  readonly honoraryNote: HonoraryNote;
  readonly lineTypeValues: TypeCodeTranslation[];
  readonly unitTypeValues: TypeCodeTranslation[];
  readonly vatPercentageValues: TypeCodeTranslation[];
  readonly currentLanguage: string;
  readonly caseBlocked: boolean;
  readonly caseCode: string;
  readonly instructionId: number;
  readonly validations: BusinessValidation[];
}

interface PropsFromDispatch {
  readonly addHonoraryNoteLine: typeof addHonoraryNoteLine;
  readonly saveHonoraryNote: typeof saveHonoraryNote;
  readonly updateHonoraryReference: typeof updateHonoraryReference;
  readonly deleteHonoraryNoteLine: typeof deleteHonoraryNoteLine;
  readonly updateHonoraryStructuredMessage: typeof updateHonoraryStructuredMessage;
}

interface State {
  readonly description: string;
  readonly lineTypeTC: string;
  readonly number: string;
  readonly unitTypeTC: string;
  readonly unitPrice: string;
  readonly vatPercentageTC: string;
}

type AllProps = PropsFromState & ConnectedReduxProps & PropsFromDispatch;

class HonoraryNoteDetail extends React.Component<AllProps, State> {
  private localization: Ii18n;
  constructor(props: AllProps) {
    super(props);
    this.localization = getI18n();
    this.state = {
      number: '',
      description: '',
      lineTypeTC: '',
      unitPrice: '',
      unitTypeTC: '',
      vatPercentageTC: ''
    };
  }

  private get amount() {
    const amount = honoraryNoteLogic.calculateAmount(this.state.unitPrice, this.state.number);
    if (amount === 0) {
      return (
        <span className={`${styles.text} ${styles.blue}`}>
          {this.localization.get('details.honoraryNote.amount')}
        </span>
      );
    }
    return (
      <BelfiusCurrencyText className={`${styles.input} ${styles.blue}`}
        value={amount} />
    );
  }
  private get vatAmount() {
    const vatAmount = honoraryNoteLogic.calculateVat(this.state.unitPrice, this.state.number, this.state.vatPercentageTC);
    if (vatAmount === 0) {
      return (
        <span className={`${styles.text} ${styles.blue}`}>
          {this.localization.get('details.honoraryNote.vatAmount')}
        </span>
      );
    }
    return (
      <BelfiusCurrencyText className={`${styles.input} ${styles.blue}`}
        value={vatAmount} />
    );
  }

  private get subtotal() {
    const subtotal = honoraryNoteLogic.calculateSubtotal(this.state.unitPrice, this.state.number, this.state.vatPercentageTC);
    if (subtotal === 0) {
      return (
        <span className={`${styles.text} ${styles.blue}`}>
          {this.localization.get('details.honoraryNote.subtotal')}
        </span>
      );
    }
    return (
      <BelfiusCurrencyText className={`${styles.input} ${styles.blue}`}
        value={subtotal} />
    );
  }

  private resetState() {
    this.setState(
      { number: '',
        description: '',
        lineTypeTC: '',
        unitPrice: '',
        unitTypeTC: '',
        vatPercentageTC: ''
       });
  }


  addHonoraryLine() {
    if (this.props.caseBlocked) { return; }
    if (!this.props.honoraryNote.locked && this.state.lineTypeTC && this.state.number && this.state.unitPrice && this.state.unitTypeTC && this.state.vatPercentageTC) {
      this.props.addHonoraryNoteLine(
        this.props.caseCode,
        {
          honoraryNoteId: this.props.honoraryNote.honoraryNoteId,
          honoraryNoteLineId: 0,
          description: this.state.description,
          lineTypeTC: this.state.lineTypeTC,
          number: this.state.number,
          unitPrice: this.state.unitPrice,
          unitTypeTC: this.state.unitTypeTC,
          vatPercentage: this.state.vatPercentageTC
        });
      this.resetState();
    }
  }

  render() {
    const linesOverview = this.props.honoraryNote.honoraryNoteLines.map(x => {
      return (
        <div className={`${styles.row} ${styles.overviewLineBorder}`} key={x.honoraryNoteLineId}>
          <div className={styles.item2x}>
            <span className={styles.text}>
              {getTypeCodeTranslation(this.props.lineTypeValues, this.props.currentLanguage, x.lineTypeTC)}
            </span>
          </div>
          <div className={styles.item2x}>
            <span className={styles.text}>{x.description}</span>
          </div>
          <div className={`${styles.item2x}`}>
            <span className={`${styles.text}`}>
              {x.number} {getTypeCodeTranslation(this.props.unitTypeValues, this.props.currentLanguage, x.unitTypeTC)}&nbsp;
              <BelfiusCurrencyText
                prefix={'x € '}
                value={x.unitPrice} />
            </span>&nbsp;
              </div>
          <div className={`${styles.item} ${styles.text}`}>
            <span>{x.vatPercentage}%</span>
          </div>
          <div className={`${styles.item} ${styles.text} ${styles.alignRight}`}>
            <BelfiusCurrencyText
              prefix={'€ '}
              value={honoraryNoteLogic.calculateAmount(x.unitPrice, x.number)} />
          </div>
          <div className={`${styles.item} ${styles.text} ${styles.alignRight}`}>
            <BelfiusCurrencyText
              prefix={'€ '}
              value={honoraryNoteLogic.calculateVat(x.unitPrice, x.number, x.vatPercentage)} />
          </div>
          <div className={`${styles.item} ${styles.text} ${styles.alignRight}`}>
            <BelfiusCurrencyText
              prefix={'€ '}
              value={honoraryNoteLogic.calculateSubtotal(x.unitPrice, x.number, x.vatPercentage)} />
          </div>
          <div className={styles.removebutton}
            onClick={() => {
              if (!this.props.honoraryNote.locked) {
                this.props.deleteHonoraryNoteLine(x.honoraryNoteLineId);
              }
            }}
          >
            <img alt="" src={require('../../../../assets/images/remove_33x33.png')} />
          </div>
        </div>
      );
    });

    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.row}>
            <div className={`${styles.item} ${styles.border}`}>
              <span className={`${styles.text} ${styles.white}`}>
                {this.localization.get('details.honoraryNote.date')}
              </span>
            </div>
            <div className={`${styles.item} ${styles.border}`}>
              <div className={`${styles.text} ${styles.white}`}>
                <DatePicker
                  value={this.props.honoraryNote.messageDate}
                  locale={this.props.currentLanguage}
                  disabled={this.props.honoraryNote.locked || this.props.caseBlocked}
                  onChange={(date: Date) => {
                    this.props.saveHonoraryNote({ ...this.props.honoraryNote, messageDate: date.toISOString(), instructionId: this.props.instructionId });
                  }}
                />
              </div>
            </div>
          </div>
          <div className={`${styles.row}`}>
            <div className={`${styles.item} ${styles.border}`}>
              <span className={`${styles.text} ${styles.white}`}>
                {this.localization.get('details.honoraryNote.honoraryReference')}
              </span>
            </div>
            <div className={`${styles.item} ${styles.border}`}>
              <input className={styles.input}
                disabled={this.props.honoraryNote.locked || this.props.caseBlocked}
                placeholder={this.localization.get('details.honoraryNote.honoraryReference')}
                value={this.props.honoraryNote.honoraryReference || ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  this.props.updateHonoraryReference(event.target.value);
                }}
                onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
                  this.props.saveHonoraryNote({
                    ...this.props.honoraryNote, honoraryReference: event.target.value, instructionId: this.props.instructionId
                  });
                }}
              />
            </div>
          </div>
          <div className={`${styles.row} ${styles.bottomPadding}`}>
            <div className={`${styles.item} ${styles.border}`}>
              <span className={`${styles.text} ${styles.white}`}>
                {this.localization.get('details.honoraryNote.structuredMessage')}
              </span>
            </div>
            <div className={`${styles.item} ${styles.border}`}>
              {this.props.honoraryNote.structuredMessage &&
              this.props.validations.find(item => item.businessValidationType === BusinessValidationType.hnStructuredMessage && item.hasError === true) &&
              <div className={styles.notValid}></div>}
              <FormattedInputStructuredMessage
                className='{styles.input}'
                disabled={this.props.honoraryNote.locked || this.props.caseBlocked}
                value={this.props.honoraryNote.structuredMessage} 
                onChange={this.props.updateHonoraryStructuredMessage}
                onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
                  this.props.saveHonoraryNote({
                    ...this.props.honoraryNote, structuredMessage: event.target.value, instructionId: this.props.instructionId
                  });
                }}
                />
              </div>
            </div>
          {!this.props.honoraryNote.locked && !this.props.caseBlocked ?
          <div>
          <div className={styles.row}>
            <div className={`${styles.item} ${styles.border}`}>
              <span className={`${styles.text} ${styles.white}`}>
                {this.localization.get('details.honoraryNote.lineType')}
              </span>
            </div>
            <div className={`${styles.item} ${styles.border}`}>
              <FormControl className={styles.formControl}>
                <Select
                  value={this.state.lineTypeTC}
                  onChange={(event: SelectChangeEvent<string>) => {
                    this.setState({ lineTypeTC: event.target.value });
                  }}
                  disabled={this.props.honoraryNote.locked || this.props.caseBlocked}
                  displayEmpty
                  className={styles.select}
                >
                  <MenuItem value='' key='' disabled>
                    {this.localization.get('details.honoraryNote.chooseLineType')}
                  </MenuItem>
                  {getTypeCodesForLanguage(this.props.lineTypeValues, this.props.currentLanguage).map(o =>
                    <MenuItem
                      value={o.typeCodeValue}
                      key={o.typeCodeValue}>{o.translation}
                    </MenuItem>)}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.row}>
            <div className={`${styles.item} ${styles.border}`}>
              <span className={`${styles.text} ${styles.white}`}>
                {this.localization.get('details.honoraryNote.description')}
              </span>
            </div>
            <div className={`${styles.item} ${styles.border}`}>
              <input className={styles.input}
                disabled={this.props.honoraryNote.locked || this.props.caseBlocked}
                placeholder={this.localization.get('details.honoraryNote.description')}
                value={this.state.description}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  this.setState({ description: event.target.value });
                }}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={`${styles.item} ${styles.border}`}>
              <span className={`${styles.text} ${styles.white}`}>
                {this.localization.get('details.honoraryNote.number')}
              </span>
            </div>
            <div className={`${styles.item} ${styles.border}`}>
              <BelfiusDecimalInput className={styles.input}
                disabled={this.props.honoraryNote.locked || this.props.caseBlocked}
                placeholder={this.localization.get('details.honoraryNote.number')}
                value={this.state.number}
                onValueChange={(values: { value: string }) => {
                  this.setState({ number: values.value });
                }}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={`${styles.item} ${styles.border}`}>
              <span className={`${styles.text} ${styles.white}`}>
                {this.localization.get('details.honoraryNote.unitType')}
              </span>
            </div>
            <div className={`${styles.item} ${styles.border}`}>
              <FormControl className={styles.formControl}>
                <Select
                  value={this.state.unitTypeTC}
                  onChange={(event: SelectChangeEvent<string>) => {
                    this.setState({ unitTypeTC: event.target.value });
                  }}
                  disabled={this.props.honoraryNote.locked || this.props.caseBlocked}
                  displayEmpty
                  className={styles.select}
                >
                  <MenuItem value='' key='' disabled>
                    {this.localization.get('details.honoraryNote.chooseUnitType')}
                  </MenuItem>
                  {getTypeCodesForLanguage(this.props.unitTypeValues, this.props.currentLanguage).map(o =>
                    <MenuItem
                      value={o.typeCodeValue}
                      key={o.typeCodeValue}>{o.translation}
                    </MenuItem>)}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.row}>
            <div className={`${styles.item} ${styles.border}`}>
              <span className={`${styles.text} ${styles.white}`}>
                {this.localization.get('details.honoraryNote.unitPrice')}
              </span>
            </div>
            <div className={`${styles.item} ${styles.border}`}>
              <BelfiusCurrencyInput className={styles.input}
                disabled={this.props.honoraryNote.locked}
                value={this.state.unitPrice}
                placeholder={this.localization.get('details.honoraryNote.unitPrice')}
                onValueChange={(values: { value: string }) => {
                  this.setState({ unitPrice: values.value });
                }}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={`${styles.item} ${styles.border}`}>
              <span className={`${styles.text} ${styles.white}`}>
                {this.localization.get('details.honoraryNote.vat')}
              </span>
            </div>
            <div className={`${styles.item} ${styles.border}`}>
              <FormControl className={styles.formControl}>
                <Select
                  value={this.state.vatPercentageTC}
                  onChange={(event: SelectChangeEvent<string>) => {
                    this.setState({ vatPercentageTC: event.target.value });
                  }}
                  disabled={this.props.honoraryNote.locked}
                  displayEmpty
                  className={styles.select}
                >
                  <MenuItem value='' key='' disabled>
                    {this.localization.get('details.honoraryNote.chooseVat')}
                  </MenuItem>
                  {getTypeCodesForLanguage(this.props.vatPercentageValues, this.props.currentLanguage).map(o =>
                    <MenuItem
                      value={o.typeCodeValue}
                      key={o.typeCodeValue}>{o.translation}
                    </MenuItem>)}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.row}>
            <div className={`${styles.item2x} ${styles.border}`}>
              {this.amount}
            </div>
            <div className={`${styles.item2x} ${styles.border}`}>
              {this.vatAmount}
            </div>
            <div className={`${styles.item2x} ${styles.border}`}>
              {this.subtotal}
            </div>
            <div className={`${styles.item} ${styles.border}`}>
              <div className={styles.addbutton}
                onClick={() => { this.addHonoraryLine(); }}
              >
                <img alt="" className={styles.lineImage} src={require('../../../../assets/images/add_85x85.png')} />
              </div>
            </div>
          </div>
          </div>
          : '' }
          <div className={styles.border}>
            <div className={styles.linesOverview}>
              <div className={styles.row}>
                <div className={styles.item2x}>
                </div>
                <div className={styles.item2x}>
                </div>
                <div className={styles.item2x}>
                </div>
                <div className={styles.item}>
                </div>
                <div className={styles.item}>
                  <span className={`${styles.text} ${styles.alignCenter}`}>{this.localization.get('details.honoraryNote.amount')}</span>
                </div>
                <div className={styles.item}>
                  <span className={`${styles.text} ${styles.alignCenter}`}>{this.localization.get('details.honoraryNote.vat')}</span>
                </div>
                <div className={`${styles.item} ${styles.rightMargin}`}>
                  <span className={`${styles.text} ${styles.alignCenter}`}>{this.localization.get('details.honoraryNote.subtotal')}</span>
                </div>
              </div>
              {linesOverview}
              <div className={`${styles.row} ${styles.rightMargin}`}>
                <div className={styles.item6x}></div>
                <div className={`${styles.item} ${styles.text} ${styles.alignRight}`}>
                  <span>{this.localization.get('details.honoraryNote.total')}</span>
                </div>
                <div className={`${styles.item} ${styles.text} ${styles.alignRight}`}>
                  <BelfiusCurrencyText
                    prefix={'€ '}
                    value={honoraryNoteLogic.getTotal(this.props.honoraryNote.honoraryNoteLines)} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ details: { detailsHonoraryNote }, shared, caseState: { selectedCase } }: ApplicationState): PropsFromState => ({
  honoraryNote: detailsHonoraryNote.selectedHonoraryNote,
  currentLanguage: shared.currentLanguage,
  lineTypeValues: detailsHonoraryNote.lineTypeValues,
  unitTypeValues: detailsHonoraryNote.unitTypeValues,
  vatPercentageValues: detailsHonoraryNote.vatPercentageValues,
  caseBlocked: selectedCase.blocked,
  caseCode: selectedCase.caseCode,
  instructionId: selectedCase.instructionId,
  validations: detailsHonoraryNote.validations,
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
  addHonoraryNoteLine: (caseCode: string, honoraryNoteLine: HonoraryNoteLine) => dispatch(addHonoraryNoteLine(caseCode, honoraryNoteLine)),
  saveHonoraryNote: (honoraryNote: HonoraryNote) => dispatch(saveHonoraryNote(honoraryNote)),
  updateHonoraryReference: (honoraryReference: string) => dispatch(updateHonoraryReference(honoraryReference)),
  deleteHonoraryNoteLine: (honoraryNoteLineId: number) => dispatch(deleteHonoraryNoteLine(honoraryNoteLineId)),
  updateHonoraryStructuredMessage: (structuredMessage: string) => dispatch(updateHonoraryStructuredMessage(structuredMessage))
});

export default connect(mapStateToProps, mapDispatchToProps)(HonoraryNoteDetail);
